import React from "react";

import { Box, Heading, Link, Text, List, ListItem, VStack } from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";

function scrollToBlogSection(e) {
  e.preventDefault();
  const el = document.getElementById("blog-section");
  if (el) {
    el.scrollIntoView({ behavior: "smooth" });
    window.history.replaceState(null, '', '#blog-section');
  } else {
    window.location.href = "/blog#blog-section";
  }
}

export default function UnderstandingAPRInterestTotalLoanCost() {
  const [loanAmount, setLoanAmount] = React.useState(0);
  const [interestRate, setInterestRate] = React.useState(0);
  const [loanTerm, setLoanTerm] = React.useState(0);
  const [monthlyPayment, setMonthlyPayment] = React.useState(0);
  const [totalPayment, setTotalPayment] = React.useState(0);
  const [totalInterest, setTotalInterest] = React.useState(0);

  const handleLoanAmountChange = (e) => setLoanAmount(Number(e.target.value));
  const handleInterestRateChange = (e) => setInterestRate(Number(e.target.value));
  const handleLoanTermChange = (e) => setLoanTerm(Number(e.target.value));

  React.useEffect(() => {
    const principal = Number(loanAmount);
    const rate = Number(interestRate);
    const years = Number(loanTerm);
    if (principal > 0 && rate > 0 && years > 0) {
      const r = rate / 100 / 12;
      const n = years * 12;
      const monthly = (principal * r) / (1 - Math.pow(1 + r, -n));
      const total = monthly * n;
      const interest = total - principal;
      setMonthlyPayment(monthly.toFixed(2));
      setTotalPayment(total.toFixed(2));
      setTotalInterest(interest.toFixed(2));
    } else {
      setMonthlyPayment(0);
      setTotalPayment(0);
      setTotalInterest(0);
    }
  }, [loanAmount, interestRate, loanTerm]);

  return (
    <Box maxW="1000px" mx="auto" px={{ base: 4, md: 8 }} py={{ base: 20, md: 32 }}>
      <VStack spacing={6} align="start">
        <Link
          as="button"
          onClick={scrollToBlogSection}
          aria-label="Back to Blog"
          position="fixed"
          top={{ base: "84px", md: "96px" }}
          left={{ base: 4, md: 8 }}
          zIndex="sticky"
          bg="white"
          color="teal.600"
          fontWeight="semibold"
          border="1px solid"
          borderColor="teal.100"
          borderRadius="full"
          px={{ base: 0, sm: 4 }}
          py={{ base: 0, sm: 2 }}
          w={{ base: "42px", sm: "auto" }}
          h={{ base: "42px", sm: "auto" }}
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          boxShadow="sm"
          _hover={{ textDecoration: "none", bg: "teal.50" }}
        >
          <Text as="span" display={{ base: "inline", sm: "none" }}>←</Text>
          <Text as="span" display={{ base: "none", sm: "inline" }}>← Back to Blog</Text>
        </Link>
        <Heading as="h1" size="xl" mb={2} mt={16}>
          Understanding APR Interest and Total Loan Cost
        </Heading>
        <Text fontSize="sm" color="gray.500">
          By Heavenly • March 2026 • 6 mins read
        </Text>
        <Text>
          <b>A guide for borrowers, homeowners, and anyone considering a loan</b>
        </Text>
        <Text>
          When you borrow money, the true cost is more than just the amount you receive. Understanding how APR (Annual Percentage Rate), interest, and loan terms work can save you thousands of dollars and help you make smarter financial decisions. This guide breaks down the essentials in a clear, practical way.
        </Text>
        <Heading as="h2" size="md">🔎 What Is APR?</Heading>
        <Text>
          APR stands for <b>Annual Percentage Rate</b>. It represents the yearly cost of borrowing money, including interest and most fees. APR gives you a more complete picture than just the interest rate alone.
        </Text>
        <Text>
          <b>Example:</b> If you borrow $10,000 at a 6% APR for 5 years, you’ll pay more than $10,000 back — the APR tells you how much extra you’ll pay each year.
        </Text>
        <Heading as="h2" size="md">💡 Why APR Matters</Heading>
        <List spacing={2} pl={4} styleType="disc">
          <ListItem>Lets you compare loans easily</ListItem>
          <ListItem>Includes most fees, not just interest</ListItem>
          <ListItem>Helps you spot hidden costs</ListItem>
        </List>
        <Heading as="h2" size="md">📈 How Interest Works</Heading>
        <Text>
          Interest is the price you pay to borrow money. It’s usually expressed as a percentage of the loan amount. The higher the rate, the more you pay.
        </Text>
        <Text>
          <b>Simple Interest:</b> Calculated only on the original amount you borrow.
        </Text>
        <Text>
          <b>Compound Interest:</b> Calculated on the original amount <i>plus</i> any interest already added. Most loans use simple interest, but credit cards often use compound interest.
        </Text>
        {/* Calculator heading and description removed as requested */}
        {/* Loan Calculator removed as requested */}
        <Heading as="h2" size="md">📊 What Affects Your Loan Cost?</Heading>
        <List spacing={2} pl={4} styleType="disc">
          <ListItem><b>Loan Amount:</b> Borrowing more means higher payments and more interest overall.</ListItem>
          <ListItem><b>Interest Rate:</b> Even a small difference in rate can add up over time.</ListItem>
          <ListItem><b>Loan Term:</b> Longer terms mean lower monthly payments but more total interest.</ListItem>
          <ListItem><b>Fees:</b> Origination, processing, and other fees increase your total cost.</ListItem>
        </List>
        <Heading as="h2" size="md">📝 Tips for Borrowers</Heading>
        <List spacing={2} pl={4} styleType="disc">
          <ListItem>Compare APRs, not just interest rates</ListItem>
          <ListItem>Ask about all fees before signing</ListItem>
          <ListItem>Pay extra when possible to reduce interest</ListItem>
          <ListItem>Read the fine print on loan agreements</ListItem>
        </List>
        <Heading as="h2" size="md">🧠 Final Thoughts</Heading>
        <Text>
          Understanding APR, interest, and total loan cost empowers you to borrow smarter and avoid costly surprises. Use the calculator above to see how different rates and terms affect your payments — and always shop around for the best deal!
        </Text>

        <Box mt={10} width="100%" textAlign="center">
          <Heading as="h3" size="md" mb={3} color="teal.700">Get an instant loan now with Swift Credit</Heading>
          <Link as={RouterLink} to="/apply" onClick={() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
            <Box as="button" bg="teal.500" color="white" px={8} py={3} borderRadius="md" fontWeight="bold" _hover={{ bg: "teal.600" }}>
              Get a Loan
            </Box>
          </Link>
        </Box>
      </VStack>
    </Box>
  );
}